import React, { useState } from 'react'
import { toast } from 'react-toastify'

import {
    Button,
    IconButton,
    Modal,
    Paper,
    Slide,
    TextField,
    useTheme,
    Typography,
    Box,
} from '@material-ui/core'
import { DeleteForever, CloudUpload, GetApp, Info } from '@material-ui/icons'

import { Container } from '../../components/Container'
import { DropFiles } from '../../components/DropFiles'
import {
    formatFileSize,
    validateFacilitiesInspectionFileSize,
} from '../../helpers/inspectionUpload'
import { downloadFacilitiesInspectionTemplate } from '../../helpers/facilitiesInspectionTemplate'
import { useFacilitiesInspectionUpload } from '../../hooks/useFacilitiesInspectionUpload'

interface Props {
    open: boolean
    onClose: () => void
}

const MODAL_W = 650
const MODAL_H = 'calc(100vh - 300px)'
const HEADER_H = 70

export const UploadFacilitiesInspectionModal = (props: Props) => {
    const { open, onClose } = props

    const theme = useTheme()
    const {
        uploadSheet,
        validateFile,
        validateEmailFormat,
        isUploading,
        uploadProgress,
    } = useFacilitiesInspectionUpload()

    const [email, setEmail] = useState('')
    const [file, setFile] = useState<File | null>(null)
    const [isDownloadingTemplate, setIsDownloadingTemplate] = useState(false)

    const handleFileChange = (files: File[]) => {
        if (files.length > 0) {
            setFile(files[0])
        }
    }

    const checkValid = () => {
        if (file === null) {
            return false
        }
        if (email.trim() === '') {
            return false
        }
        return true
    }

    const handleUpload = async () => {
        if (!checkValid()) {
            toast.error('Please select a file and enter your email')
            return
        }

        if (!file) {
            toast.error('Please select a file')
            return
        }

        if (!validateFile(file)) {
            if (!validateFacilitiesInspectionFileSize(file)) {
                toast.error('File size must be less than 10MB')
            } else {
                toast.error(
                    'Please select a valid file type (CSV, XLSX, or XLS)',
                )
            }
            return
        }

        if (!validateEmailFormat(email)) {
            toast.error('Please enter a valid email address')
            return
        }

        try {
            const result = await uploadSheet({
                file,
                email: email.trim(),
            })

            onClose()
            setFile(null)
            setEmail('')
        } catch (error: any) {
            // Error is already handled by the hook
        }
    }

    const handleClose = () => {
        if (!isUploading) {
            onClose()
            setFile(null)
            setEmail('')
        }
    }

    const handleDownloadSample = async () => {
        setIsDownloadingTemplate(true)
        try {
            downloadFacilitiesInspectionTemplate()
            toast.success('Sample template downloaded successfully!')
        } catch (error) {
            toast.error('Error downloading template. Please try again.')
        } finally {
            setIsDownloadingTemplate(false)
        }
    }

    return (
        <Modal
            open={open}
            onClose={handleClose}
            style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
            }}
        >
            <Slide direction="up" in={open}>
                <Paper
                    elevation={8}
                    style={{
                        borderRadius: 12,
                        width: MODAL_W,
                        height: MODAL_H,
                        maxHeight: 'calc(100vh - 100px)',
                        overflow: 'hidden',
                        border: `1px solid ${theme.palette.grey[200]}`,
                    }}
                >
                    <Container
                        style={{
                            flexDirection: 'column',
                            height: '100%',
                        }}
                    >
                        {/* Header */}
                        <Container
                            style={{
                                background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 100%)`,
                                height: HEADER_H,
                                padding: theme.spacing(0, 3),
                                alignItems: 'center',
                                justifyContent: 'space-between',
                                color: 'white',
                            }}
                        >
                            <Container style={{ alignItems: 'center' }}>
                                <CloudUpload
                                    style={{
                                        marginRight: theme.spacing(2),
                                        fontSize: 28,
                                    }}
                                />
                                <Typography
                                    variant="h5"
                                    style={{
                                        fontWeight: 600,
                                        color: 'white',
                                    }}
                                >
                                    Upload Facilities Inspection Sheet
                                </Typography>
                            </Container>
                        </Container>

                        {/* Content */}
                        <Container
                            style={{
                                flex: 1,
                                flexDirection: 'column',
                                padding: theme.spacing(4),
                                background: theme.palette.background.default,
                                overflow: 'auto',
                                minHeight: 0,
                                maxHeight: 'calc(100vh - 300px)',
                            }}
                        >
                            {/* File Upload Section */}
                            <Box style={{ marginBottom: theme.spacing(2) }}>
                                <Container
                                    style={{
                                        alignItems: 'center',
                                        justifyContent: 'space-between',
                                        marginBottom: theme.spacing(1),
                                    }}
                                >
                                    <Typography
                                        variant="h6"
                                        style={{
                                            fontWeight: 600,
                                            color: theme.palette.text.primary,
                                        }}
                                    >
                                        Select File
                                    </Typography>
                                    <Button
                                        variant="outlined"
                                        color="primary"
                                        startIcon={<GetApp />}
                                        onClick={handleDownloadSample}
                                        disabled={
                                            isUploading || isDownloadingTemplate
                                        }
                                        style={{
                                            textTransform: 'none',
                                            fontWeight: 500,
                                            padding: theme.spacing(0.5, 2),
                                            borderRadius: 6,
                                            borderColor:
                                                theme.palette.primary.main,
                                            color: theme.palette.primary.main,
                                        }}
                                    >
                                        {isDownloadingTemplate
                                            ? 'Downloading...'
                                            : 'Download Template'}
                                    </Button>
                                </Container>
                                <Typography
                                    variant="body2"
                                    style={{
                                        color: theme.palette.text.secondary,
                                        marginBottom: theme.spacing(1.5),
                                    }}
                                >
                                    Upload your facilities inspection data in
                                    CSV, XLSX, or XLS format. Download the
                                    template to see the required format.
                                </Typography>
                                <Container
                                    style={{ padding: theme.spacing(1) }}
                                >
                                    <DropFiles
                                        onDrop={handleFileChange}
                                        displayString="Drop your file here or click to browse"
                                        fileTypes={{
                                            'text/csv': ['.csv'],
                                            'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': [
                                                '.xlsx',
                                            ],
                                            'application/vnd.ms-excel': [
                                                '.xls',
                                            ],
                                        }}
                                    />
                                </Container>

                                {/* Template Information */}
                                <Box
                                    style={{
                                        marginTop: theme.spacing(2),
                                        padding: theme.spacing(2),
                                        backgroundColor:
                                            theme.palette.info.light + '10',
                                        borderRadius: 8,
                                        border: `1px solid ${theme.palette.info.light}`,
                                    }}
                                >
                                    <Container
                                        style={{
                                            alignItems: 'center',
                                            marginBottom: theme.spacing(1),
                                        }}
                                    >
                                        <Info
                                            style={{
                                                marginRight: theme.spacing(1),
                                                color: theme.palette.info.dark,
                                                fontSize: 20,
                                            }}
                                        />
                                        <Typography
                                            variant="subtitle2"
                                            style={{
                                                fontWeight: 600,
                                                color: theme.palette.info.dark,
                                            }}
                                        >
                                            Template Format
                                        </Typography>
                                    </Container>
                                    <Typography
                                        variant="body2"
                                        style={{
                                            color: theme.palette.text.secondary,
                                            lineHeight: 1.5,
                                        }}
                                    >
                                        The template includes columns for:{' '}
                                        <strong>Inspection Type</strong>,{' '}
                                        <strong>Frequency</strong>,{' '}
                                        <strong>Unit Name</strong>,{' '}
                                        <strong>Area Name</strong>,{' '}
                                        <strong>Inventory Item</strong>, and{' '}
                                        <strong>Description</strong>, . Download
                                        the template to see the exact format and
                                        sample data for your facilities
                                        inspection requirements.
                                    </Typography>
                                </Box>
                            </Box>

                            {/* File Display */}
                            {file && (
                                <Box style={{ marginBottom: theme.spacing(2) }}>
                                    <Container
                                        style={{
                                            border: `2px solid ${theme.palette.success.main}`,
                                            borderRadius: 8,
                                            padding: theme.spacing(1.5),
                                            alignItems: 'center',
                                            background:
                                                theme.palette.success.light +
                                                '10',
                                        }}
                                    >
                                        <CloudUpload
                                            style={{
                                                marginRight: theme.spacing(1),
                                                color:
                                                    theme.palette.success.main,
                                            }}
                                        />
                                        <Typography
                                            variant="body1"
                                            style={{
                                                flex: 1,
                                                fontWeight: 500,
                                                color:
                                                    theme.palette.text.primary,
                                            }}
                                        >
                                            {file.name}
                                        </Typography>
                                        <Typography
                                            variant="caption"
                                            style={{
                                                color:
                                                    theme.palette.text
                                                        .secondary,
                                                marginRight: theme.spacing(1),
                                            }}
                                        >
                                            {formatFileSize(file.size)}
                                        </Typography>
                                        <IconButton
                                            onClick={() => {
                                                setFile(null)
                                            }}
                                            disabled={isUploading}
                                            size="small"
                                            style={{
                                                color: theme.palette.error.main,
                                            }}
                                        >
                                            <DeleteForever />
                                        </IconButton>
                                    </Container>
                                </Box>
                            )}

                            {/* Email Section */}
                            <Box>
                                <Typography
                                    variant="h6"
                                    style={{
                                        fontWeight: 600,
                                        marginBottom: theme.spacing(1),
                                        color: theme.palette.text.primary,
                                    }}
                                >
                                    Contact Information
                                </Typography>
                                <Typography
                                    variant="body2"
                                    style={{
                                        color: theme.palette.text.secondary,
                                        marginBottom: theme.spacing(1.5),
                                    }}
                                >
                                    Enter your email address to receive the
                                    processing results
                                </Typography>
                                <TextField
                                    fullWidth
                                    label="Email Address"
                                    variant="outlined"
                                    value={email}
                                    onChange={(e) => setEmail(e.target.value)}
                                    placeholder="your.email@example.com"
                                    disabled={isUploading}
                                    style={{
                                        marginBottom: theme.spacing(1),
                                    }}
                                    InputProps={{
                                        style: {
                                            borderRadius: 8,
                                        },
                                    }}
                                />
                            </Box>
                        </Container>

                        {/* Footer */}
                        <Container
                            style={{
                                borderTop: `1px solid ${theme.palette.divider}`,
                                height: 60,
                                padding: theme.spacing(1, 3),
                                alignItems: 'center',
                                justifyContent: 'flex-end',
                                background: theme.palette.background.paper,
                            }}
                        >
                            <Button
                                onClick={handleClose}
                                disabled={isUploading}
                                style={{
                                    marginRight: theme.spacing(2),
                                    textTransform: 'none',
                                    fontWeight: 500,
                                    padding: theme.spacing(1, 3),
                                }}
                            >
                                Cancel
                            </Button>
                            <Button
                                variant="contained"
                                color="primary"
                                onClick={handleUpload}
                                disabled={!checkValid() || isUploading}
                                style={{
                                    background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 100%)`,
                                    color: 'white',
                                    textTransform: 'none',
                                    fontWeight: 600,
                                    padding: theme.spacing(0.75, 3),
                                    borderRadius: 8,
                                    boxShadow:
                                        '0 4px 12px rgba(0, 140, 133, 0.3)',
                                }}
                            >
                                {isUploading ? 'Uploading...' : 'Upload Sheet'}
                            </Button>
                        </Container>
                    </Container>
                </Paper>
            </Slide>
        </Modal>
    )
}
